// src/pages/Shop/ThankYouPage.js
//
// Thank-you page after redirect back from Przelewy24.
// =============================================================================
//
// RESPONSIBILITIES
// 1) Read query params from URL:
//      - orderId=<numeric>
//      - token=<unguessable public token>   (currently Order.session_id)
// 2) Poll backend until payment is final:
//      GET /api/shop/orders/:orderId?token=...
// 3) Render UX states:
//      - pending / processing
//      - paid (voucher created / email sent or queued)
//      - failed / canceled
// 4) Best practice: clear client-side cart after success.
//
// IMPORTANT SECURITY CONTRACT (CURRENT APP DEVELOPMENT)
// -----------------------------------------------------
// - getOrderStatus is a PUBLIC endpoint and requires token.
// - If token missing/mismatch, backend responds 404 to avoid leaking existence.
// - Backend now masks PII in public response (buyerEmail/buyerName/recipientName).
//   Therefore this page must treat these fields as "display-safe but masked".
//
// VOUCHER NUMBER BEST PRACTICE
// ----------------------------
// - NEVER display internal voucherId as "voucher number".
// - Display customer-facing `voucherNo` (or `voucher.voucherNo`) if available.
//
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link } from "react-router-dom";
import { getOrderStatus } from "../../services/shopService";
import PublicLanguageDropdown from "../../components/PublicLanguageDropdown";
import "./ThankYouPage.css";

// Polling controls
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_TIME_MS = 2 * 60 * 1000; // 2 minutes

// Keep these keys consistent with ShopPage.js
const CART_STORAGE_KEY = "hb_shop_cart_v1";
const LAST_PURCHASE_AT_KEY = "hb_last_successful_purchase_at";

/**
 * useQuery()
 * Small hook wrapper returning URLSearchParams for current location.
 */
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * normalizeStatus(raw)
 *
 * Normalize backend status into one of:
 *  - "paid"
 *  - "failed"
 *  - "canceled"
 *  - "pending" (new/processing/pending/unknown)
 *
 * Note:
 * - Backend currently uses `order.status` like "pending" / "paid" / "error".
 * - If you ever introduce new statuses, they will map to "pending" by default.
 */
function normalizeStatus(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (s === "paid") return "paid";
  if (s === "failed" || s === "error") return "failed";
  if (s === "canceled" || s === "cancelled") return "canceled";
  return "pending";
}

/**
 * getCustomerVoucherNumber(order)
 *
 * Extract the CUSTOMER-FACING voucher number from backend response.
 * We intentionally do NOT use `voucherId` (internal DB identifier).
 */
function getCustomerVoucherNumber(order) {
  if (!order) return null;

  const candidates = [
    order?.voucherNo,
    order?.voucher?.voucherNo,
    // Legacy / optional:
    order?.voucherNumber,
    order?.voucherCode,
    order?.voucherPublicId,
    order?.voucherNumberHuman,
  ]
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);

  return candidates.length > 0 ? candidates[0] : null;
}

/**
 * safeText(value)
 * Defensive helper to prevent rendering "undefined" / "null".
 */
function safeText(value) {
  const s = value == null ? "" : String(value).trim();
  return s || "";
}

/**
 * formatMoney(amount, currency)
 * Uses fixed 2 decimals and app currency fallback.
 */
function formatMoney(amount, currency, language = "en") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  const c = currency || "PLN";
  const locale = String(language || "en").startsWith("pl") ? "pl-PL" : "en-US";

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n) + ` ${c}`;
  } catch {
    return `${n.toFixed(2)} ${c}`;
  }
}

const ThankYouPage = () => {
  const { t, i18n } = useTranslation("shop");

  // ThankYouPage is a public page reached from an external payment redirect.
  // It therefore uses the current public i18n runtime language and never
  // depends on an authenticated user profile.
  const currentPublicLanguage =
    i18n.resolvedLanguage || i18n.language || "en";

  const handlePublicLanguageChange = (selection) => {
    if (!selection?.language) return;
    i18n.changeLanguage(selection.language);
  };

  const query = useQuery();

  // Required params (CURRENT CONTRACT)
  const orderIdParam = query.get("orderId");
  const tokenParam = query.get("token");

  const [order, setOrder] = useState(null);
  const [pollErrorKey, setPollErrorKey] = useState("");
  const [finished, setFinished] = useState(false);

  const normalizedStatus = useMemo(
    () => normalizeStatus(order?.status),
    [order?.status]
  );

  const customerVoucherNumber = useMemo(
    () => getCustomerVoucherNumber(order),
    [order]
  );

  // Customer should return to the SHOP.
  // We include ?purchased=1 so ShopPage can also auto-clear cart (belt & suspenders).
  const shopReturnUrl = "/shop?purchased=1";

  // -------------------------------------------------------------------------
  // Polling logic
  // -------------------------------------------------------------------------
  useEffect(() => {
    // If orderId or token missing -> cannot poll successfully (backend will 404).
    if (!orderIdParam || !tokenParam) {
      setFinished(true);
      return;
    }

    let isMounted = true;
    let intervalId = null;
    const startTime = Date.now();

    const fetchOnce = async () => {
      try {
        // IMPORTANT: token is required by backend
        const data = await getOrderStatus(orderIdParam, tokenParam);

        if (!isMounted) return;

        setOrder(data);
        setPollErrorKey("");

        const status = normalizeStatus(data?.status);

        // Stop polling once final.
        if (status === "paid" || status === "failed" || status === "canceled") {
          setFinished(true);
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }

        // Still pending - timeout protection
        const elapsed = Date.now() - startTime;
        if (elapsed > MAX_POLL_TIME_MS) {
          setFinished(true);
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          setPollErrorKey("thankYou.errors.paymentStillProcessing");
        }
      } catch (err) {
        console.error("[ThankYouPage] Error fetching order status:", err);

        if (!isMounted) return;

        // For 404 (token mismatch / missing / order does not exist),
        // show a user-friendly message.
        const status = err?.response?.status;
        if (status === 404) {
          setFinished(true);
          setPollErrorKey("thankYou.errors.orderNotFound");
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }

        setPollErrorKey("thankYou.errors.statusLoadFailed");
      }
    };

    // First fetch immediately, then interval polling
    fetchOnce();
    intervalId = setInterval(fetchOnce, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [orderIdParam, tokenParam]);

  // -------------------------------------------------------------------------
  // BEST PRACTICE: clear cart after PAID
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (normalizedStatus !== "paid") return;

    try {
      // Remove cart so a user coming back to the shop doesn't see old items.
      window.localStorage.removeItem(CART_STORAGE_KEY);

      // Store a "last successful purchase" timestamp (ShopPage can use it as fallback).
      window.localStorage.setItem(LAST_PURCHASE_AT_KEY, String(Date.now()));
    } catch (e) {
      // Not fatal, just log.
      console.warn("[ThankYouPage] Could not clear cart localStorage:", e);
    }
  }, [normalizedStatus]);

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderMissingParams = () => {
    // Missing orderId and/or token means user likely opened the page directly
    // rather than arriving via P24 urlReturn redirect.
    return (
      <div className="ty-section">
        <h1 className="ty-title">{t("thankYou.missingParams.title")}</h1>
        <p className="ty-text">{t("thankYou.missingParams.text")}</p>
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <div className="ty-section">
        <div className="ty-status-pill ty-status-pending">
          <span className="spinner" /> {t("thankYou.status.pending") }
        </div>

        <h1 className="ty-title">{t("thankYou.loading.title")}</h1>
        <p className="ty-text">{t("thankYou.loading.text")}</p>
      </div>
    );
  };

  const renderPaid = () => {
    // Backend masks these fields, so they are safe to render as-is,
    // but they may look like "***@***.***".
    const buyerEmailMasked = safeText(order?.buyerEmail);

    return (
      <div className="ty-section">
        <div className="ty-status-pill ty-status-success">
          ✓ {t("thankYou.status.paid")}
        </div>

        <h1 className="ty-title">{t("thankYou.paid.title")}</h1>

        <p className="ty-text">
          {t("thankYou.paid.thankYouPrefix")}{" "}
          <strong>{formatMoney(order?.amount, order?.currency, currentPublicLanguage)}</strong>
          {t("thankYou.paid.thankYouSuffix")}
        </p>

        {/* Display masked email (backend provides masked value) */}
        {buyerEmailMasked ? (
          <p className="ty-text">
            {t("thankYou.paid.emailSentToPrefix")}{" "}
            <strong>{buyerEmailMasked}</strong>
            {t("thankYou.paid.emailSentToSuffix")}
          </p>
        ) : (
          <p className="ty-text">
            {t("thankYou.paid.emailSentGeneric")}
          </p>
        )}

        {/* Customer-facing voucher number/code (only if backend returns it) */}
        {customerVoucherNumber ? (
          <p className="ty-text">
            {t("thankYou.paid.voucherNumberPrefix")} <strong>{customerVoucherNumber}</strong>
          </p>
        ) : (
          <p className="ty-text ty-text-small">
            {t("thankYou.paid.voucherNumberInEmail")}
          </p>
        )}

        {/* Optional: show email delivery state (helpful for support/UX) */}
        {order?.email?.status && (
          <p className="ty-text ty-text-small">
            {t("thankYou.paid.emailDeliveryStatusPrefix")} <strong>{order.email.status}</strong>
            {typeof order?.email?.attempts === "number"
              ? t("thankYou.paid.emailAttempts", { count: order.email.attempts })
              : ""}
          </p>
        )}

        <p className="ty-text ty-text-small">
          {t("thankYou.paid.spamHint")}
        </p>
      </div>
    );
  };

  const renderFailed = () => {
    return (
      <div className="ty-section">
        <div className="ty-status-pill ty-status-failed">
          ⚠ {t("thankYou.status.failed")}
        </div>

        <h1 className="ty-title">{t("thankYou.failed.title")}</h1>

        <p className="ty-text">
          {t("thankYou.failed.statusPrefix")} <strong>#{safeText(order?.id)}</strong>{" "}
          {t("thankYou.failed.statusMiddle")} <strong>{safeText(order?.status)}</strong>.
        </p>

        <p className="ty-text">
          {t("thankYou.failed.contactHint")}
        </p>
      </div>
    );
  };

  const renderPending = () => {
    return (
      <div className="ty-section">
        <div className="ty-status-pill ty-status-pending">
          <span className="spinner" /> {t("thankYou.status.pending") }
        </div>

        <h1 className="ty-title">{t("thankYou.pending.title")}</h1>

        <p className="ty-text">
          {t("thankYou.pending.textPrefix")} <strong>#{safeText(order?.id)}</strong>.
        </p>

        <p className="ty-text ty-text-small">
          {t("thankYou.pending.autoRefreshHint")}
        </p>
      </div>
    );
  };

  const renderStatusSection = () => {
    // Missing orderId or token => cannot poll securely
    if (!orderIdParam || !tokenParam) return renderMissingParams();

    // First fetch / slow network
    if (!order) return renderLoading();

    // State-based UI
    if (normalizedStatus === "paid") return renderPaid();
    if (normalizedStatus === "failed" || normalizedStatus === "canceled") return renderFailed();
    return renderPending();
  };

  return (
    <div className="ty-page">
      <div className="ty-card">
        <div className="ty-public-language-row">
          <PublicLanguageDropdown
            value={currentPublicLanguage}
            onChange={handlePublicLanguageChange}
            className="ty-public-language-dropdown"
            style={{ marginBottom: 0 }}
          />
        </div>

        <header className="ty-header">
          <div className="ty-logo-circle">HB</div>
          <div>
            <div className="ty-header-title">{t("thankYou.header.title")}</div>
            <div className="ty-header-subtitle">
              {t("thankYou.header.subtitle")}
            </div>
          </div>
        </header>

        {renderStatusSection()}

        {pollErrorKey && (
          <div className="ty-alert ty-alert-error">{t(pollErrorKey)}</div>
        )}

        <footer className="ty-footer">
          <p className="ty-footer-text">
            {t("thankYou.footer.helpText")}
          </p>

          {/* Primary return action is "back to shop" */}
          <div className="ty-actions">
            <Link to={shopReturnUrl} className="ty-primary-link">
              {t("thankYou.actions.backToShop")}
            </Link>

            {/* Public homepage (outside the /app router) */}
            <a
              href="https://hb.konin.pl"
              className="ty-secondary-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("thankYou.actions.homepage")}
            </a>
          </div>

          {/* Debug/support hint: show orderId parameter (safe) */}
          {finished && orderIdParam && (
            <div className="ty-footer-hint">
              {t("thankYou.footer.orderIdPrefix")} <strong>{orderIdParam}</strong>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ThankYouPage;


